import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ForcedPasswordChange } from './ForcedPasswordChange';
import { WorkspaceLoading } from './WorkspaceLoading';
import { SessionUnavailable } from './SessionUnavailable';

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, agent, sessionUnavailable, retrySession } = useAuth();
  const location = useLocation();

  // Wait for the initial /me check before deciding where to navigate.
  // Without this, cookie-authenticated users would briefly see a redirect to
  // /login on hard reload before the /me response arrives.
  if (loading) {
    return <WorkspaceLoading />;
  }

  // The session check failed for a reason that is not "signed out" (offline, 429, 5xx): keep retrying, never bounce
  // a possibly signed-in person to the login page.
  if (!isAuthenticated && sessionUnavailable) {
    return <SessionUnavailable onRetry={retrySession} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A super-admin-provisioned temp password: block the ENTIRE dashboard
  // (Layout/children never mount) until the user sets their own password.
  if (agent?.mustChangePassword) {
    return <ForcedPasswordChange />;
  }

  return <>{children}</>;
}
